import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, CheckCircleIcon, LoaderIcon, AlertCircleIcon, CheckIcon } from 'lucide-react';
type Status = 'idle' | 'loading' | 'success' | 'error';
type Fields = {
  firstName: string;
  email: string;
  countryCode: string;
  whatsapp: string;
};
type GuideModalProps = {
  open: boolean;
  onClose: () => void;
};
const countryCodes = [{
  code: '+49',
  label: '🇩🇪 +49'
}, {
  code: '+43',
  label: '🇦🇹 +43'
}, {
  code: '+41',
  label: '🇨🇭 +41'
}];
export function GuideModal({
  open,
  onClose
}: GuideModalProps) {
  const [fields, setFields] = useState<Fields>({
    firstName: '',
    email: '',
    countryCode: '+49',
    whatsapp: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof Fields, string>>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);
  const update = (key: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFields(prev => ({
      ...prev,
      [key]: e.target.value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
    if (status === 'error') setStatus('idle');
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const nextErrors: Partial<Record<keyof Fields, string>> = {};
    if (!fields.firstName.trim()) nextErrors.firstName = 'Bitte gib deinen Vor- und Nachnamen an.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) nextErrors.email = 'Bitte gib eine gültige E-Mail-Adresse ein.';
    if (!/^[0-9\s/-]{7,20}$/.test(fields.whatsapp.trim())) nextErrors.whatsapp = 'Bitte gib deine Handynummer an.';
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setStatus('error');
      return;
    }
    setStatus('loading');
    window.setTimeout(() => setStatus('success'), 900);
  };
  const inputClass = (key: keyof Fields) => `w-full rounded-full border bg-neutral-50 px-5 py-3 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-gold focus:bg-cream ${errors[key] ? 'border-red-400' : 'border-neutral-200'}`;
  return <AnimatePresence data-id="element-242" data-mp="1qkh6kshxpdvn">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[60] flex items-center justify-center bg-neutral-900/50 p-4 backdrop-blur-sm" onClick={onClose} data-id="element-243" data-mp="1qliv9zrvhyvu">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="guide-modal-title" initial={{
        opacity: 0,
        scale: 0.94,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.22,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative w-full max-w-md overflow-hidden rounded-xl bg-cream p-6 shadow-2xl sm:p-8" data-id="element-244" data-mp="35ylp4c5c4tlh">
            <span aria-hidden="true" className="gold-glitter-layer-static" data-id="element-245" data-mp="1i2xcb33uyy9o" />
            <span aria-hidden="true" className="gold-glitter-extra" data-id="element-246" data-mp="1i147ltct2o4b" />

            <button type="button" onPointerUp={e => {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }} onClick={e => {
          e.stopPropagation();
          onClose();
        }} aria-label="Schließen" className="absolute right-2 top-2 z-20 flex h-11 w-11 touch-manipulation items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700" data-id="element-247" data-mp="1e0k7cgwxr4dy">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-248" data-mp="3v9n8jcoxcwk2" />
            </button>

            <div className="relative z-10 text-center" data-id="element-249" data-mp="3vbq74l457y0e">
              <p className="text-sm font-semibold text-gold" data-id="element-250" data-mp="44driaiwrsml">Nur noch 1 Schritt fehlt...</p>
              <h2 id="guide-modal-title" className="font-heading mt-2 text-2xl font-bold text-neutral-900 sm:text-3xl" data-id="element-251" data-mp="92owa6ucjcxy">
                Wohin dürfen wir dir deinen Fahrplan schicken?
              </h2>
              <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-600" data-id="element-252" data-mp="3ch1dguluda0j">
                Du bekommst den 15-seitigen Guide sofort per E-Mail – kostenlos und unverbindlich.
              </p>
            </div>

            {status === 'success' ? <div role="status" className="relative z-10 mt-6 flex items-start gap-3 rounded-2xl bg-hot-50 p-5" data-id="element-253" data-mp="2osi8op203fd7">
                <CheckCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-hot-300" aria-hidden="true" data-id="element-254" data-mp="2trczad67m87v" />
                <p className="text-sm text-neutral-800" data-id="element-255" data-mp="1ax02n07f6ps0">
                  <strong className="block text-base font-semibold" data-id="element-256" data-mp="16veutt9qt1oo">
                    Yes {fields.firstName.trim()}, geschafft!
                  </strong>
                  Schau auch im Spam-Ordner nach.
                </p>
              </div> : <form onSubmit={handleSubmit} noValidate className="relative z-20 mt-6 space-y-3" data-id="element-257" data-mp="wzuzp930670z">
                <div data-id="element-258" data-mp="s0s8arfy3zbh">
                  <input type="text" autoComplete="given-name" value={fields.firstName} onChange={update('firstName')} placeholder="Vor- und Nachname" aria-label="Vor- und Nachname" aria-invalid={Boolean(errors.firstName)} className={inputClass('firstName')} data-id="element-259" data-mp="n7yyvq9j82t2" />
                </div>

                <div data-id="element-260" data-mp="49v5c464occd">
                  <input type="email" autoComplete="email" value={fields.email} onChange={update('email')} placeholder="Deine geschäftliche E-Mail-Adresse" aria-label="Deine geschäftliche E-Mail-Adresse" aria-invalid={Boolean(errors.email)} className={inputClass('email')} data-id="element-261" data-mp="4bfpczutgcvz" />
                </div>

                <div className="flex gap-0" data-id="element-262" data-mp="dcqrwu3btcjh">
                  <div className="relative w-28 shrink-0" data-id="element-263" data-mp="ias9tbp0n5td">
                    <button type="button" onClick={() => setCountryDropdownOpen(!countryDropdownOpen)} className="flex h-full w-full items-center justify-between rounded-l-full border border-r-0 border-neutral-200 bg-neutral-50 py-3 pl-4 pr-2 text-sm text-neutral-900 outline-none focus:border-gold focus:bg-cream" data-id="element-264" data-mp="iaa83zjl3ze2">
                      <span data-id="element-265" data-mp="113ns59lrelew">{countryCodes.find(c => c.code === fields.countryCode)?.label}</span>
                      <svg className="h-4 w-4 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-266" data-mp="2lpvm90johwpe">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-267" data-mp="2vhrvn7r7t98x" />
                      </svg>
                    </button>
                    
                    {countryDropdownOpen && <>
                        <div className="fixed inset-0 z-10" onClick={() => setCountryDropdownOpen(false)} data-id="element-268" data-mp="22tm8kecov85x" />
                        <ul className="absolute left-0 top-full z-20 mt-1 max-h-48 w-full overflow-auto rounded-2xl border border-neutral-200 bg-cream py-1 shadow-lg" data-id="element-269" data-mp="2cs1qbqptt52r">
                          {countryCodes.map(c => <li key={c.code} data-id="element-270" data-mp="3o8jphykt5lt3">
                              <button type="button" onClick={() => {
                      setFields(prev => ({
                        ...prev,
                        countryCode: c.code
                      }));
                      setCountryDropdownOpen(false);
                    }} className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-neutral-100" data-id="element-271" data-mp="3j7vr2ru922ou">
                                {c.label}
                              </button>
                            </li>)}
                        </ul>
                      </>}
                  </div>
                  <input type="tel" inputMode="tel" autoComplete="tel-local" value={fields.whatsapp} onChange={update('whatsapp')} placeholder="Deine Handynummer" aria-label="Deine Handynummer" aria-invalid={Boolean(errors.whatsapp)} className={`w-full rounded-r-full border bg-neutral-50 px-4 py-3 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-gold focus:bg-cream ${errors.whatsapp ? 'border-red-400' : 'border-neutral-200'}`} data-id="element-272" data-mp="2992vvzn3b74p" />
                </div>

                <button type="submit" disabled={status === 'loading'} className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-hot-300 px-6 py-4 text-sm font-semibold text-white shadow-md transition-colors hover:bg-hot-400 disabled:opacity-70" data-id="element-273" data-mp="3tsydhsy0t09j">
                  {status === 'loading' && <LoaderIcon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-274" data-mp="gbp8fc2az05c" />}
                  Jetzt kostenlos herunterladen
                </button>

                {status === 'error' && <ul role="alert" className="space-y-1 pt-1" data-id="element-275" data-mp="311eofseqsrzm">
                    {(Object.keys(errors) as (keyof Fields)[]).map(key => errors[key] ? <li key={key} className="flex items-center gap-2 text-xs font-medium text-red-500" data-id="element-276" data-mp="1leqtqdaprbml">
                          <AlertCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-277" data-mp="1vakay9zdntap" />
                          {errors[key]}
                        </li> : null)}
                  </ul>}
              </form>}

            <div className="relative z-10 mt-6 flex flex-col items-center gap-1.5 text-xs text-neutral-600" data-id="element-278" data-mp="2j1g00zcg8ae9">
              <div className="flex items-center gap-1.5" data-id="element-279" data-mp="2e34p0laf3tug">
                <CheckIcon className="h-3.5 w-3.5 text-hot-300" data-id="element-280" data-mp="on5b6qvutg8h" />
                <span data-id="element-281" data-mp="onopzyq7joia">Von Frauen, für Frauen.</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-282" data-mp="jp3si72cbixj">
                <CheckIcon className="h-3.5 w-3.5 text-hot-300" data-id="element-283" data-mp="2vbfee4pgy9xh" />
                <span data-id="element-284" data-mp="2vbxg3guwhgcs">100% Wissen mit Praxis-Garantie!</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-285" data-mp="309gz0u2jmhb2">
                <CheckIcon className="h-3.5 w-3.5 text-hot-300" data-id="element-286" data-mp="2llm6bdojccxz" />
                <span data-id="element-287" data-mp="2llcdlic3vrol">Völlig unverbindlich und kostenfrei.</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-288" data-mp="2qj4e30qtgm4e">
                <CheckIcon className="h-3.5 w-3.5 text-hot-300" data-id="element-289" data-mp="2ckehrnbtmqh3" />
                <span data-id="element-290" data-mp="2cn0l1aroijti">Keine unerwünschte Werbung!</span>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}